import { useEffect, useRef, useCallback } from 'react';
import { useTickSound, useAccessibilityTTS } from '@/hooks';
import { useConfigStore, useModalStackStore } from '@/stores';
import { NAV_KEYS } from '@/constants/keyboard';

// Scopes where useInteractiveElementsWithTTS should not handle TTS (other hooks handle them)
const EXCLUDED_TTS_SCOPES = [
    'keyboard',
    'dropdown',
    'carousel-navigation-button',
    'floor-button',
    'number-keypad',
    'volume-button',
    'navigation-guide-button',
    'transportation-mode-button',
    'parking-search-button',
    'zoom-button',
    'nearby-poi-item',
    'nearby-poi-list-button',
    'my-location-button',
    'view-store-list-button'
] as const;

const EXCLUDED_TTS_GNB = ['gnb-menu-item'] as const;

const VOLUME_BUTTON_SCOPE = ['volume-button'] as const;

export const useInteractiveElementsWithTTS = () => {
    const { playTick } = useTickSound();
    const { speakElement, speakAriaLabel } = useAccessibilityTTS();
    const pressedElementRef = useRef<Element | null>(null);
    const voice = useConfigStore((state) => state?.config?.tts?.voice);
    const voiceRef = useRef(voice);
    const clickedElementRef = useRef<Element | null>(null);

    useEffect(() => {
        voiceRef.current = voice;
    }, [voice]);

    // Common selector for interactive elements
    const INTERACTIVE_SELECTOR =
        '[data-interactive], button[data-interactive], [role="button"][data-interactive], [role="switch"][data-interactive], [role="tab"][data-interactive], input[data-interactive], a[data-interactive], div[data-interactive]';

    // Helper function to check if element is in excluded scope
    const isInExcludedScope = useCallback((element: Element) => {
        return EXCLUDED_TTS_SCOPES.some((scope) => element.closest(`[data-tts-scope="${scope}"]`));
    }, []);

    const isInExcludedScopeGNB = useCallback((element: Element) => {
        return EXCLUDED_TTS_GNB.some((scope) => element.closest(`[data-tts-scope="${scope}"]`));
    }, []);

    const isVolumeButton = useCallback((element: Element) => {
        return VOLUME_BUTTON_SCOPE.some((scope) => element.closest(`[data-tts-scope="${scope}"]`));
    }, []);

    const isOutsideTopModal = useCallback((element: Element) => {
        const { modalStack } = useModalStackStore.getState();
        if (modalStack.length === 0) return false;
        return !element.closest('[role="dialog"], [role="alertdialog"]');
    }, []);

    // Helper function to check if element has role="button"
    const hasButtonRole = useCallback((element: Element) => {
        let current: Element | null = element;

        while (current) {
            if (current.getAttribute('type') === 'button'
                || current.getAttribute('data-playsound') === 'ting'
                || current.getAttribute('data-type') === 'button') {
                return true;
            }
            current = current.parentElement;
        }

        return false;
    }, []);

    const pressEffect = useCallback(
        (element: Element | null) => {
            if (element) {
                const playSound = element.getAttribute('data-playsound') || 'tick';
                // Skip press effect for elements in excluded scopes
                if (isVolumeButton(element)) return;

                if (element.tagName === 'DIV' && element.textContent && element.textContent.trim().length > 0) {
                    element.classList.add('is-pressed');
                    return;
                }
                element.classList.add('is-pressed');

                // Play tick sound if voice is disabled OR if element has button role
                playTick(playSound);
            }
        },
        [playTick, isInExcludedScope, hasButtonRole]
    );

    const releaseEffect = useCallback((element: Element | null) => {
        if (element) element.classList.remove('is-pressed');
    }, []);

    useEffect(() => {
        // Focus handler - immediate announcement
        const onFocusIn = (e: FocusEvent) => {
            setTimeout(() => {
                if (!voiceRef.current) return;
                const element = e.target as Element;
                if (isOutsideTopModal(element)) return;
                // Skip TTS if this element was just clicked (to prevent double reading)
                if (clickedElementRef.current === element) {
                    clickedElementRef.current = null; // Reset the flag
                    return;
                }
                // Skip TTS for elements inside excluded scopes
                if (isInExcludedScope(element)) return;
                if (element.hasAttribute('data-interactive') || element.matches(INTERACTIVE_SELECTOR)) {
                    speakElement(element);
                }
            }, 0);
        };

        // Mouse down handler for visual effects
        const onMouseDown = (e: MouseEvent) => {
            const target = (e.target as Element).closest('[data-interactive]');
            if (target) {
                if (isOutsideTopModal(target)) return;
                pressedElementRef.current = target;
                clickedElementRef.current = target;
                pressEffect(target);
            }
        };

        const onTouchStart = (e: TouchEvent) => {
            const target = (e.target as Element).closest('[data-interactive]');
            if (target) {
                if (isOutsideTopModal(target)) return;
                pressedElementRef.current = target;
                clickedElementRef.current = target;
            }
        };

        const onClick = (e: MouseEvent) => {
            const element = e.target as Element;

            if (isOutsideTopModal(element)) return;
            // Skip TTS for elements inside excluded scopes
            if (isInExcludedScope(element) || isInExcludedScopeGNB(element)) return;
            // Skip TTS for elements with role="button"
            if (hasButtonRole(element)) return;
            playTick();
            if (!voiceRef.current) return;

            const interactiveElement = element.closest(INTERACTIVE_SELECTOR);
            if (interactiveElement) {
                // Skip TTS for interactive elements with role="button"
                if (hasButtonRole(interactiveElement)) return;
                // Add delay to allow React state updates to reflect in DOM
                setTimeout(() => {
                    speakElement(interactiveElement);
                }, 150);
            }
        };

        const onKeyDown = (e: KeyboardEvent) => {
            const target = e.target as Element;
            if (isOutsideTopModal(target)) return;

            if (e.key === 'Enter') {
                const interactiveTarget = target.closest('[data-interactive]');
                if (interactiveTarget && !interactiveTarget.classList.contains('is-pressed')) {
                    pressedElementRef.current = interactiveTarget;
                    pressEffect(interactiveTarget);
                }
            }

            if (NAV_KEYS.includes(e.key)) {
                playTick();
            }

            if (e.key === 'Enter' || e.key === ' ') {
                if (!voiceRef.current) return;
                // Skip TTS for elements inside excluded scopes
                if (isInExcludedScope(target) || isInExcludedScopeGNB(target)) return;
                // Skip TTS for elements with role="button"
                if (hasButtonRole(target)) return;
                const interactiveElement = target.closest(INTERACTIVE_SELECTOR);
                if (interactiveElement) {
                    // Skip TTS for interactive elements with role="button"
                    if (hasButtonRole(interactiveElement)) return;
                    // Add delay to allow React state updates to reflect in DOM
                    setTimeout(() => {
                        speakElement(interactiveElement);
                    }, 150);
                }
            }
        };

        // Release effect handlers
        const onMouseUp = () => releaseEffect(pressedElementRef.current);
        const onMouseLeave = () => releaseEffect(pressedElementRef.current);
        const onTouchEnd = () => releaseEffect(pressedElementRef.current);
        const onTouchCancel = () => releaseEffect(pressedElementRef.current);
        const onKeyUp = (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
                releaseEffect(pressedElementRef.current);
            }
        };

        document.addEventListener('focusin', onFocusIn);
        document.addEventListener('mousedown', onMouseDown);
        document.addEventListener('touchstart', onTouchStart, { passive: true });
        document.addEventListener('click', onClick);
        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('mouseup', onMouseUp);
        document.addEventListener('mouseleave', onMouseLeave);
        document.addEventListener('touchend', onTouchEnd);
        document.addEventListener('touchcancel', onTouchCancel);
        document.addEventListener('keyup', onKeyUp);

        return () => {
            document.removeEventListener('focusin', onFocusIn);
            document.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('touchstart', onTouchStart);
            document.removeEventListener('click', onClick);
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('mouseup', onMouseUp);
            document.removeEventListener('mouseleave', onMouseLeave);
            document.removeEventListener('touchend', onTouchEnd);
            document.removeEventListener('touchcancel', onTouchCancel);
            document.removeEventListener('keyup', onKeyUp);
        };
    }, [isInExcludedScope, isInExcludedScopeGNB, hasButtonRole, speakElement, speakAriaLabel, pressEffect, releaseEffect, isOutsideTopModal]);

    return {
        pressEffect,
        releaseEffect,
        speakElement,
    };
};

export default useInteractiveElementsWithTTS;
